/**
 * "Qui mange ici" — the people at the table, accounts and children alike.
 *
 * Paprika has only ever known two kinds of person: you and your partner, both
 * of them Firebase accounts. That is enough to decide what to cook, and not
 * enough to say who liked it — a five-year-old has opinions about dinner and
 * no phone. This section is where the rest of the table gets named.
 *
 * The two accounts are listed but not editable here: they are managed through
 * pairing and the display-name field, and offering a second, divergent way to
 * rename them is how the roster and the household doc drift apart.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';
import { useHouseholdEaters } from '../../hooks/useHouseholdEaters';
import { addEater, renameEater, removeEater, type Eater } from '../../services/eatersService';
import { EaterAvatar } from './EaterAvatar';
import { EaterEditSheet } from './EaterEditSheet';
import { trackEvent } from '../../services/analyticsService';

export function EatersSection() {
  const { t } = useTranslation();
  const { table, eaters, myUid, householdId } = useHouseholdEaters(
    t('household.eaters.selfLabel')
  );
  const [editing, setEditing] = useState<Eater | 'new' | null>(null);

  const handleSubmit = useCallback(
    async (name: string) => {
      if (editing === 'new') {
        const created = await addEater(myUid, householdId, name);
        if (created) {
          trackEvent('household_eater_added', { eaterCount: eaters.length + 1 });
        }
      } else if (editing) {
        await renameEater(myUid, householdId, editing.id, name);
      }
      setEditing(null);
    },
    [editing, myUid, householdId, eaters.length]
  );

  const handleRemove = useCallback(async () => {
    if (!editing || editing === 'new') return;
    await removeEater(myUid, householdId, editing.id);
    trackEvent('household_eater_removed', { eaterCount: Math.max(0, eaters.length - 1) });
    setEditing(null);
  }, [editing, myUid, householdId, eaters.length]);

  const accounts = table.filter((m) => m.isAccount);
  const others = table.filter((m) => !m.isAccount);

  return (
    <View style={styles.wrap}>
      <Text style={styles.sectionTitle}>{t('household.eaters.sectionTitle')}</Text>

      <View style={styles.card}>
        {accounts.map((member) => (
          <View key={member.id} style={styles.row}>
            <EaterAvatar member={member} size={30} />
            <Text style={styles.name} numberOfLines={1}>
              {member.name}
            </Text>
            <Text style={styles.accountTag}>{t('household.eaters.account')}</Text>
          </View>
        ))}

        {others.length > 0 && <View style={styles.divider} />}

        {others.map((member) => {
          const eater = eaters.find((e) => e.id === member.id);
          return (
            <Pressable
              key={member.id}
              style={styles.row}
              onPress={() => eater && setEditing(eater)}
              accessibilityRole="button"
              accessibilityLabel={member.name}
            >
              <EaterAvatar member={member} size={30} />
              <Text style={styles.name} numberOfLines={1}>
                {member.name}
              </Text>
              <Feather name="chevron-right" size={18} color={Paprika.textMuted} />
            </Pressable>
          );
        })}

        {others.length === 0 && <Text style={styles.empty}>{t('household.eaters.empty')}</Text>}

        <Pressable
          style={styles.addRow}
          onPress={() => setEditing('new')}
          accessibilityRole="button"
          accessibilityLabel={t('household.eaters.addSomeone')}
        >
          <View style={styles.addIcon}>
            <Feather name="plus" size={16} color={Paprika.accent} />
          </View>
          <Text style={styles.addText}>{t('household.eaters.addSomeone')}</Text>
        </Pressable>
      </View>

      <Text style={styles.footnote}>{t('household.eaters.accountsElsewhere')}</Text>

      <EaterEditSheet
        visible={editing !== null}
        eater={editing === 'new' ? null : editing}
        onSubmit={handleSubmit}
        onRemove={editing && editing !== 'new' ? handleRemove : undefined}
        onClose={() => setEditing(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: PaprikaSpacing.lg,
  },
  sectionTitle: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: PaprikaSpacing.sm,
  },
  // Tonal surface floating on the screen background — no outline (DESIGN.md).
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    paddingVertical: PaprikaSpacing.xs,
    paddingHorizontal: PaprikaSpacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: 11,
  },
  name: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    flex: 1,
    minWidth: 0,
  },
  accountTag: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
  },
  divider: {
    height: 1,
    backgroundColor: Paprika.borderLight,
    marginVertical: PaprikaSpacing.xs,
  },
  empty: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    paddingVertical: PaprikaSpacing.sm,
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: 11,
  },
  addIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Paprika.brandTint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.accent,
  },
  footnote: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: PaprikaSpacing.sm,
  },
});
